import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/product/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TopProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopProducts = async () => {
      try {
        const response = await axios.get(`${API}/products/top-selling?limit=12`);
        setProducts(response.data.slice(0, 12));
      } catch (error) {
        console.error('Error fetching top products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTopProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-4 sm:py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={`top-skeleton-${i}`} className="animate-pulse">
                <div className="aspect-square bg-slate-100 rounded-lg" />
                <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2">
                  <div className="h-2.5 sm:h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-3 sm:h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 sm:h-4 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-4 sm:py-8 md:py-12 bg-white" data-testid="top-products-section">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-3 sm:mb-6">
          <h2 className="text-base sm:text-lg md:text-xl font-outfit font-bold text-slate-900">
            Top Selling Products
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="text-[#0047AB] hover:text-[#003380] hover:bg-blue-50 text-xs sm:text-sm font-medium px-2 sm:px-3"
            onClick={() => navigate('/products')}
            data-testid="view-all-top-selling-btn"
          >
            View All <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-0.5" />
          </Button>
        </div>

        {/* Products Grid - Responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-4">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopProducts;
